import React, { useState } from 'react'

const CodeAnalysis = () => {
  const [viewedJson, setViewedJson] = useState(null)
  const [viewedSbom, setViewedSbom] = useState(null)
  const [sbomSearchTerm, setSbomSearchTerm] = useState('')

  const sbomFiles = [
    { file_name: 'backend.xml', path: '/SBOM_reports/backend.xml' },
    { file_name: 'frontend.xml', path: '/SBOM_reports/frontend.xml' },
  ]

  const codeAnalysisFiles = [
    { file_name: 'frontend.json', path: '/Code_analysis_reports/frontend.json' },
    { file_name: 'backend.json', path: '/Code_analysis_reports/backend.json' },
  ]

  const parseXMLToComponents = (xmlText) => {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
    
    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror')
    if (parseError) {
      throw new Error('Invalid XML format')
    }

    // Find components in the XML
    const components = xmlDoc.querySelectorAll('component')
    const parsedComponents = []

    components.forEach(component => {
      const name = getElementText(component, 'name') || 'Unknown'
      const version = getElementText(component, 'version') || 'N/A'
      const type = component.getAttribute('type') || 'Unknown'
      const supplier = getElementText(component, 'supplier name') || 'Unknown'
      const description = getElementText(component, 'description') || 'No description'
      
      // Get licenses
      const licenseElements = component.querySelectorAll('license')
      const licenses = Array.from(licenseElements).map(license => {
        return getElementText(license, 'id') || getElementText(license, 'name') || 'Unknown'
      })

      // Get vulnerabilities
      const vulnElements = component.querySelectorAll('vulnerability')
      const vulnerabilities = Array.from(vulnElements).map(vuln => {
        const id = getElementText(vuln, 'id') || 'Unknown'
        const severity = getElementText(vuln, 'severity') || 'Unknown'
        return { id, severity }
      })

      // Get PURL (Package URL)
      const purl = getElementText(component, 'purl') || 'N/A'

      parsedComponents.push({
        name,
        version,
        type,
        supplier,
        description,
        licenses,
        vulnerabilities,
        purl
      })
    })

    return parsedComponents
  }

  const getElementText = (parent, selector) => {
    const element = parent.querySelector(selector)
    return element ? element.textContent.trim() : null
  }


  const handleView = async (file) => {
    if (file.file_name.endsWith('.json')) {
      const res = await fetch(file.path)
      const data = await res.json()
      setViewedJson(data)
    } else if (file.file_name.endsWith('.xml')) {
      try {
        const res = await fetch(file.path)
        const text = await res.text()
        const components = parseXMLToComponents(text)
        setViewedSbom({ fileName: file.file_name, components })
      } catch (error) {
        console.error('Error parsing XML:', error)
        // Fallback to raw XML view
        const res = await fetch(file.path)
        const text = await res.text()
        const blob = new Blob([text], { type: 'text/plain; charset=utf-8' });
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
      }
    }
  }

  const closeModal = () => {
    setViewedJson(null)
    setViewedSbom(null)
    setSbomSearchTerm('')
  }

  const filteredSbomComponents = viewedSbom?.components?.filter(component => 
    component.name.toLowerCase().includes(sbomSearchTerm.toLowerCase()) ||
    component.version.toLowerCase().includes(sbomSearchTerm.toLowerCase()) ||
    component.type.toLowerCase().includes(sbomSearchTerm.toLowerCase()) ||
    component.supplier.toLowerCase().includes(sbomSearchTerm.toLowerCase()) ||
    component.licenses.some(license => license.toLowerCase().includes(sbomSearchTerm.toLowerCase()))
  ) || []

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white'
      case 'high': return 'bg-red-500 text-white'
      case 'medium': return 'bg-yellow-500 text-white'
      case 'low': return 'bg-green-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  return (
    <div className="p-8">
      <h2 className="text-white text-2xl font-bold mb-6">SBOM Reports</h2>
      <table className="w-full text-white border border-gray-700 rounded-lg">
        <thead>
          <tr className="bg-[#1f1f1f] border-b border-gray-700">
            <th className="py-3 px-4 text-left">S.No</th>
            <th className="py-3 px-4 text-left">File Name</th>
            <th className="py-3 px-4 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sbomFiles.map((file, index) => (
            <tr key={index} className="border-b border-gray-800">
              <td className="py-3 px-4">{index + 1}</td>
              <td className="py-3 px-4">{file.file_name}</td>
              <td className="py-3 px-4">
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded"
                  onClick={() => handleView(file)}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="text-white text-2xl font-bold mt-12 mb-6">Static Code Analysis Reports</h2>
      <table className="w-full text-white border border-gray-700 rounded-lg">
        <thead>
          <tr className="bg-[#1f1f1f] border-b border-gray-700">
            <th className="py-3 px-4 text-left">S.No</th>
            <th className="py-3 px-4 text-left">File Name</th>
            <th className="py-3 px-4 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {codeAnalysisFiles.map((file, index) => (
            <tr key={index} className="border-b border-gray-800">
              <td className="py-3 px-4">{index + 1}</td>
              <td className="py-3 px-4">{file.file_name}</td>
              <td className="py-3 px-4">
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded"
                  onClick={() => handleView(file)}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* JSON Modal */}
      {viewedJson && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-white p-6 max-h-[80vh] overflow-auto rounded-md shadow-md w-11/12 max-w-4xl">
            <h3 className="text-xl font-semibold mb-4">JSON View</h3>
            <table className="table-auto w-full text-black border border-gray-300">
              <thead>
                <tr>
                  {Object.keys(viewedJson[0]).map((key) => (
                    <th className="border px-4 py-2 text-left bg-gray-200" key={key}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewedJson.map((item, index) => (
                  <tr key={index}>
                    {Object.values(item).map((value, i) => (
                      <td className="border px-4 py-2" key={i}>
                        {JSON.stringify(value)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-right">
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SBOM Modal */}
      {viewedSbom && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-white p-6 max-h-[90vh] overflow-auto rounded-md shadow-md w-11/12 max-w-7xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">SBOM Analysis: {viewedSbom.fileName}</h3>
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded" onClick={closeModal}>
                Close
              </button>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-100 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{viewedSbom.components.length}</div>
                <div className="text-sm text-gray-600">Total Components</div>
              </div>
              <div className="bg-green-100 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {new Set(viewedSbom.components.flatMap(c => c.licenses)).size}
                </div>
                <div className="text-sm text-gray-600">Unique Licenses</div>
              </div>
              <div className="bg-yellow-100 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {viewedSbom.components.reduce((sum, c) => sum + c.vulnerabilities.length, 0)}
                </div>
                <div className="text-sm text-gray-600">Vulnerabilities</div>
              </div>
              <div className="bg-purple-100 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {new Set(viewedSbom.components.map(c => c.type)).size}
                </div>
                <div className="text-sm text-gray-600">Component Types</div>
              </div>
            </div>

            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search components, versions, licenses..."
                value={sbomSearchTerm}
                onChange={(e) => setSbomSearchTerm(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Components Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-black border border-gray-300 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">Component</th>
                    <th className="border px-3 py-2 text-left">Version</th>
                    <th className="border px-3 py-2 text-left">Type</th>
                    <th className="border px-3 py-2 text-left">Supplier</th>
                    <th className="border px-3 py-2 text-left">Licenses</th>
                    <th className="border px-3 py-2 text-left">Vulnerabilities</th>
                    <th className="border px-3 py-2 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSbomComponents.map((component, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border px-3 py-2 font-medium">{component.name}</td>
                      <td className="border px-3 py-2">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {component.version}
                        </span>
                      </td>
                      <td className="border px-3 py-2">{component.type}</td>
                      <td className="border px-3 py-2">{component.supplier}</td>
                      <td className="border px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {component.licenses.map((license, i) => (
                            <span key={i} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                              {license}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="border px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {component.vulnerabilities.map((vuln, i) => (
                            <span key={i} className={`px-2 py-1 rounded text-xs ${getSeverityColor(vuln.severity)}`}>
                              {vuln.id} ({vuln.severity})
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="border px-3 py-2 max-w-xs truncate" title={component.description}>
                        {component.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredSbomComponents.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No components found matching your search criteria.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default CodeAnalysis